/**
 * Coarse analysis grid + BFS distance map for the AI.
 * Ported from `creerGrilleAnalyse` (`Jeu.c:647`) and `creerGrilleDistances`
 * (`Jeu.c:597`), which used `_Precision_Analyse_IA = 10`px cells.
 */

import type { Bike } from "../sim/bike";
import type { GridBounds } from "../sim/collision";
import type { Segment } from "../sim/trail";
import type { PlayerColor } from "../sim/types";
import { Direction } from "../sim/types";

/** Cell size, from `_Precision_Analyse_IA`. */
export const CELL = 10;

export interface CellGrid {
  cols: number;
  rows: number;
}

export function cellGrid(grid: GridBounds): CellGrid {
  return {
    cols: Math.max(1, Math.floor(grid.width / CELL)),
    rows: Math.max(1, Math.floor(grid.height / CELL)),
  };
}

export function worldToIndex(x: number, y: number, grid: GridBounds, cg: CellGrid): number {
  const col = clamp(Math.floor((x - grid.x) / CELL), 0, cg.cols - 1);
  const row = clamp(Math.floor((y - grid.y) / CELL), 0, cg.rows - 1);
  return row * cg.cols + col;
}

export function indexToCenter(
  index: number,
  grid: GridBounds,
  cg: CellGrid,
): { x: number; y: number } {
  const col = index % cg.cols;
  const row = Math.floor(index / cg.cols);
  return {
    x: grid.x + (col + 0.5) * CELL,
    y: grid.y + (row + 0.5) * CELL,
  };
}

/** Leading-edge point of a bike, in world coordinates. */
export function bikeHead(b: Bike): { x: number; y: number } {
  switch (b.dir) {
    case Direction.Up:
      return { x: b.x + b.w / 2, y: b.y };
    case Direction.Down:
      return { x: b.x + b.w / 2, y: b.y + b.h };
    case Direction.Left:
      return { x: b.x, y: b.y + b.h / 2 };
    case Direction.Right:
      return { x: b.x + b.w, y: b.y + b.h / 2 };
    default:
      return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
  }
}

/** Step `n` cells from `index` in a direction. Returns -1 if it leaves the grid. */
export function stepIndex(index: number, dir: Direction, n: number, cg: CellGrid): number {
  let col = index % cg.cols;
  let row = Math.floor(index / cg.cols);
  switch (dir) {
    case Direction.Up:
      row -= n;
      break;
    case Direction.Down:
      row += n;
      break;
    case Direction.Left:
      col -= n;
      break;
    case Direction.Right:
      col += n;
      break;
    default:
      break;
  }
  if (col < 0 || col >= cg.cols || row < 0 || row >= cg.rows) return -1;
  return row * cg.cols + col;
}

export interface DangerInput {
  grid: GridBounds;
  segments: readonly Segment[];
  bikes: ReadonlyArray<{ bike: Bike; alive: boolean; index: number }>;
  /** The AI whose fresh same-colour trail is safe to ignore. */
  selfIndex: number;
  selfColor: PlayerColor;
  /** `_Duree_Vie_Mur`. Walls fresher than `trailLife - 2` of your colour don't count. */
  trailLife: number;
}

/**
 * Fills `out` (length rows*cols) with 0 for free cells and a positive "how long
 * this stays blocked" value for occupied ones. Mirrors `creerGrilleAnalyse`:
 * a wall counts if it is another colour or older than `trailLife - 2`.
 * Also stamps other live bikes and a couple of cells ahead of them.
 */
export function buildDanger(input: DangerInput, cg: CellGrid, out: Int16Array): void {
  out.fill(0);
  const { grid } = input;
  const lifeThreshold = input.trailLife - 2;

  for (const seg of input.segments) {
    const hostile = seg.life < lifeThreshold || seg.color !== input.selfColor;
    if (!hostile) continue;
    const c0 = clamp(Math.floor((seg.x - grid.x) / CELL), 0, cg.cols - 1);
    const c1 = clamp(Math.floor((seg.x + seg.w - 1 - grid.x) / CELL), 0, cg.cols - 1);
    const r0 = clamp(Math.floor((seg.y - grid.y) / CELL), 0, cg.rows - 1);
    const r1 = clamp(Math.floor((seg.y + seg.h - 1 - grid.y) / CELL), 0, cg.rows - 1);
    const value = Math.max(1, Math.ceil(seg.life));
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const i = r * cg.cols + c;
        if (out[i]! < value) out[i] = value;
      }
    }
  }

  for (const entry of input.bikes) {
    if (entry.index === input.selfIndex || !entry.alive) continue;
    const b = entry.bike;
    const here = worldToIndex(b.x + b.w / 2, b.y + b.h / 2, grid, cg);
    out[here] = Math.max(out[here]!, 200);
    for (let ahead = 1; ahead <= 2; ahead++) {
      const idx = stepIndex(worldToIndex(bikeHead(b).x, bikeHead(b).y, grid, cg), b.dir, ahead, cg);
      if (idx >= 0) out[idx] = Math.max(out[idx]!, 200);
    }
  }
}

/**
 * 4-directional BFS distance map from `start` over free cells (`danger[i] === 0`).
 * Writes distances into `dist` (-1 = unreachable). `queue` is scratch of length
 * >= rows*cols. Returns the number of reachable cells.
 */
export function floodFill(
  danger: Int16Array,
  start: number,
  cg: CellGrid,
  dist: Int16Array,
  queue: Int32Array,
): number {
  dist.fill(-1);
  dist[start] = 0;
  let head = 0;
  let tail = 0;
  queue[tail++] = start;
  let reached = 1;
  const { cols, rows } = cg;
  const total = cols * rows;

  while (head < tail) {
    const cur = queue[head++]!;
    const d = dist[cur]! + 1;
    const col = cur % cols;

    const up = cur - cols;
    if (up >= 0 && danger[up] === 0 && dist[up] === -1) {
      dist[up] = d;
      queue[tail++] = up;
      reached++;
    }
    const down = cur + cols;
    if (down < total && danger[down] === 0 && dist[down] === -1) {
      dist[down] = d;
      queue[tail++] = down;
      reached++;
    }
    if (col > 0) {
      const left = cur - 1;
      if (danger[left] === 0 && dist[left] === -1) {
        dist[left] = d;
        queue[tail++] = left;
        reached++;
      }
    }
    if (col < cols - 1) {
      const right = cur + 1;
      if (danger[right] === 0 && dist[right] === -1) {
        dist[right] = d;
        queue[tail++] = right;
        reached++;
      }
    }
  }
  return reached;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
